import React, { useState, useEffect } from 'react';
import PlanSelect from './PlanSelect';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const now = new Date();

function Field({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export default function AddSubscriptionModal({ restaurant, onClose, onSuccess }) {
  const { t } = useSettings();
  const months = t('months');
  const years  = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  const [mode,    setMode]    = useState('single');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [result,  setResult]  = useState(null);

  const [form, setForm] = useState({
    selectedPlan:  '',
    month:         now.getMonth() + 1,
    year:          now.getFullYear(),
    fromMonth:     1,
    fromYear:      now.getFullYear(),
    toMonth:       now.getMonth() + 1,
    toYear:        now.getFullYear(),
    totalAmount:   '',
    paidAmount:    '',
    paymentMethod: 'cash',
    invoiceNumber: '',
    notes:         '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Auto-fill plan + amounts from restaurant on open ─────
  useEffect(() => {
    if (!restaurant?.planId) return;
    const planId = restaurant.planId?._id || restaurant.planId;
    axios.get('/api/plans')
      .then(res => {
        const match = res.data.find(p => String(p._id) === String(planId));
        if (match) {
          setForm(f => ({
            ...f,
            selectedPlan: match.name,
            totalAmount:  String(match.price),
            paidAmount:   String(match.price),
          }));
        }
      })
      .catch(() => {});
  }, [restaurant?.planId]);

  // ── When plan changes, update totalAmount + paidAmount ───
  const handlePlanChange = (planId, planName, planPrice) => {
    if (planPrice !== null) {
      setForm(f => ({
        ...f,
        selectedPlan: planId,
        totalAmount:  String(planPrice),
        paidAmount:   String(planPrice),
      }));
    } else {
      set('selectedPlan', planId);
    }
  };

  const remaining = () => {
    const total = parseFloat(form.totalAmount) || 0;
    const paid  = parseFloat(form.paidAmount)  || 0;
    return Math.max(0, total - paid);
  };

  const statusBadge = () => {
    const total = parseFloat(form.totalAmount) || 0;
    const paid  = parseFloat(form.paidAmount)  || 0;
    if (!total || !paid) return null;
    if (paid >= total)  return { label: t('paid'),    cls: 'badge-green' };
    if (paid > 0)       return { label: t('partial'), cls: 'badge-yellow' };
    return               { label: t('unpaid'),  cls: 'badge-red' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setResult(null);
    setLoading(true);
    try {
      if (mode === 'single') {
        await axios.post('/api/subscriptions', {
          restaurantId:  restaurant._id,
          month:         form.month,
          year:          form.year,
          totalAmount:   Number(form.totalAmount),
          paidAmount:    Number(form.paidAmount),
          paymentMethod: form.paymentMethod,
          invoiceNumber: form.invoiceNumber,
          notes:         form.notes,
        });
        onSuccess();
      } else {
        const res = await axios.post('/api/subscriptions/bulk', {
          restaurantId:  restaurant._id,
          fromMonth:     form.fromMonth,
          fromYear:      form.fromYear,
          toMonth:       form.toMonth,
          toYear:        form.toYear,
          totalAmount:   Number(form.totalAmount),
          paidAmount:    Number(form.paidAmount),
          paymentMethod: form.paymentMethod,
          invoiceNumber: form.invoiceNumber,
          notes:         form.notes,
        });
        setResult(res.data);
        if (res.data.created > 0) onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving payment');
    } finally {
      setLoading(false);
    }
  };

  const badge = statusBadge();

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 22, marginBottom: 3 }}>{t('recordPayment')}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{restaurant.name}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* Mode toggle */}
        <div className="toggle-pill" style={{ marginBottom: 20, width: 'fit-content' }}>
          <button className={mode === 'single' ? 'active' : ''} onClick={() => { setMode('single'); setError(''); setResult(null); }}>
            {t('singlePayment')}
          </button>
          <button className={mode === 'bulk' ? 'active' : ''} onClick={() => { setMode('bulk'); setError(''); setResult(null); }}>
            {t('bulkPayment')}
          </button>
        </div>

        <form onSubmit={handleSubmit}>

          {/* Date selection */}
          {mode === 'single' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label={t('month')}>
                <select value={form.month} onChange={e => set('month', +e.target.value)}>
                  {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </Field>
              <Field label={t('year')}>
                <select value={form.year} onChange={e => set('year', +e.target.value)}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </Field>
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                ℹ️ {t('bulkHint')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label={t('fromMonth')}>
                  <select value={form.fromMonth} onChange={e => set('fromMonth', +e.target.value)}>
                    {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </Field>
                <Field label={t('year')}>
                  <select value={form.fromYear} onChange={e => set('fromYear', +e.target.value)}>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
                <Field label={t('toMonth')}>
                  <select value={form.toMonth} onChange={e => set('toMonth', +e.target.value)}>
                    {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </Field>
                <Field label={t('year')}>
                  <select value={form.toYear} onChange={e => set('toYear', +e.target.value)}>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
              </div>
            </>
          )}

          {/* Payment details box */}
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '14px', marginBottom: 16, marginTop: 4 }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Payment Details
            </div>

            {/* Plan selector — auto-selected from restaurant, admin can change */}
            <Field label={t('selectPlan')}>
              <PlanSelect
                value={form.selectedPlan}
                onChange={handlePlanChange}
              />
            </Field>

            {/* Auto-fill hint */}
            {form.selectedPlan && form.totalAmount && (
              <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: -8, marginBottom: 12, paddingLeft: 2 }}>
                ✓ {t('planAutoFill')} · {Number(form.totalAmount).toLocaleString()} UZS
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
              <Field label={t('totalAmount')}>
                <input type="number" min="0" placeholder="500000" required
                  value={form.totalAmount} onChange={e => set('totalAmount', e.target.value)} />
              </Field>
              <Field label={t('paidAmount')}>
                <input type="number" min="0" placeholder="500000" required
                  value={form.paidAmount} onChange={e => set('paidAmount', e.target.value)} />
              </Field>
            </div>

            {/* Live remaining + status */}
            {form.totalAmount && form.paidAmount && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>
                  {t('remainingAmount')}: <strong style={{ color: remaining() > 0 ? 'var(--red)' : 'var(--green)' }}>
                    {remaining().toLocaleString()} UZS
                  </strong>
                </span>
                {badge && (
                  <span className={`badge ${badge.cls === 'badge-yellow' ? '' : badge.cls}`}
                    style={badge.cls === 'badge-yellow' ? { background: 'rgba(201,168,76,0.15)', color: 'var(--accent)' } : {}}>
                    {badge.label}
                  </span>
                )}
              </div>
            )}
          </div>

          <Field label={t('paymentMethod')}>
            <select value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
              <option value="cash">{t('cash')}</option>
              <option value="card">{t('card')}</option>
              <option value="transfer">{t('transfer')}</option>
            </select>
          </Field>

          <Field label={t('invoiceNumber')}>
            <input type="text" placeholder={t('invoicePlaceholder')} value={form.invoiceNumber} onChange={e => set('invoiceNumber', e.target.value)} />
          </Field>

          <Field label={t('notes')}>
            <textarea rows={2} placeholder={t('notesPlaceholder')} value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'none', marginBottom: 0 }} />
          </Field>

          {error && (
            <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 14, border: '1px solid rgba(224,92,92,0.2)' }}>
              {error}
            </div>
          )}

          {result && (
            <div style={{ background: 'var(--green-bg)', color: 'var(--green)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 14, border: '1px solid rgba(76,175,130,0.25)' }}>
              ✓ {result.created} {t('bulkSuccess')}
              {result.skipped?.length > 0 && ` · ${result.skipped.length} ${t('bulkSkipped')}`}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1 }}>{t('cancel')}</button>
            <button type="submit" className="btn-primary" style={{ flex: 2 }} disabled={loading}>
              {loading ? t('saving') : t('recordPayment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
